import { useState, useEffect, useCallback } from "react";
import {
  X, MapPin, Mail, Phone, Briefcase, Users, UserPlus,
  ChevronRight, Plus, DollarSign, Calendar, Percent,
  ArrowRight, FileText, Loader, UserCheck, Search, ChevronDown, Link, Edit2, Trash2, Archive
} from "lucide-react";
import AddContactPersonModal from "./AddContactPersonModal";
import AllContactPersonsModal from "./AllContactPersonsModal";
import DealFormModal from "./DealFormModal";
import {
  getDealsByContact,
  listUsers,
  getContactPersons,
  addContactPerson,
  updateContactPerson,
  deleteContactPerson,
  setPrimaryContactPerson,
} from "../api/services";
import { useAuth } from "../Auth-Context/AuthContext";
import africanCountries from "../data/countries";

const STAGES = [
  { id: "prospecting",              name: "Prospecting",            color: "#8B5CF6" },
  { id: "lead_qualification",       name: "Lead Qualification",     color: "#EC4899" },
  { id: "discovery_pain_points",    name: "Discovery/Pain Points",  color: "#F59E0B" },
  { id: "demo_pitch",               name: "Demo/Pitch",             color: "#10B981" },
  { id: "proposal",                 name: "Proposal",               color: "#3B82F6" },
  { id: "negotiation",              name: "Negotiation",            color: "#6366F1" },
  { id: "closed_won",               name: "Closed Won",             color: "#059669" },
  { id: "closed_lost",              name: "Closed Lost",            color: "#EF4444" },
  { id: "customer",                 name: "Customer",               color: "#009DB9" },
];

const DEAL_STAGE_STYLES = {
  PROSPECTING:            { color: "#8B5CF6", bg: "#ede9fe" },
  LEAD_QUALIFICATION:     { color: "#EC4899", bg: "#fce7f3" },
  DISCOVERY_PAIN_POINTS:  { color: "#F59E0B", bg: "#fef3c7" },
  DEMO_PITCH:             { color: "#10B981", bg: "#d1fae5" },
  PROPOSAL:               { color: "#3B82F6", bg: "#dbeafe" },
  NEGOTIATION:            { color: "#6366F1", bg: "#e0e7ff" },
  CLOSED_WON:             { color: "#059669", bg: "#d1fae5" },
  CLOSED_LOST:            { color: "#EF4444", bg: "#fee2e2" },
  CUSTOMER:               { color: "#009DB9", bg: "#e0f7fa" },
};

function normalizeUrl(url) {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function SectionDivider() {
  return <hr className="my-1 border-neutral-100" />;
}

function SectionTitle({ children }) {
  return (
    <h4 className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">
      {children}
    </h4>
  );
}

// ─── Email validation ──────────────────────────────────────────────────────
const isValidEmail = (email) => {
  const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return regex.test(email);
};

// ─── Searchable User Dropdown Component ──────────────────────────────────────
function SearchableUserDropdown({ value, onChange, users, isLoading, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const selectedUser = users.find(u => u.id === value);
  const displayName = selectedUser 
    ? `${selectedUser.firstName} ${selectedUser.lastName}`
    : "";
  
  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = (user.email || "").toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });
  
  const handleSelect = (user) => {
    onChange(user.id);
    setIsOpen(false);
    setSearchTerm("");
  };
  
  return (
    <div className="relative">
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full h-9 border rounded-lg px-3 text-[13px] outline-none transition-colors flex items-center justify-between cursor-pointer ${
          disabled 
            ? "bg-neutral-100 border-neutral-200 text-neutral-500 cursor-not-allowed"
            : "bg-white border-neutral-200 hover:border-[#009DB9] focus:border-[#009DB9]"
        }`}
      >
        <span className={!displayName ? "text-neutral-400" : "text-neutral-800"}>
          {displayName || "Select a sales representative..."}
        </span>
        <ChevronDown size={14} className={`text-neutral-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </div>
      
      {isOpen && !disabled && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 w-full mt-1 overflow-hidden bg-white border rounded-lg shadow-lg border-neutral-200">
            <div className="p-2 border-b border-neutral-100">
              <div className="relative">
                <Search size={14} className="absolute -translate-y-1/2 left-3 top-1/2 text-neutral-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full h-8 pl-8 pr-3 text-[12px] border border-neutral-200 rounded-lg outline-none focus:border-[#009DB9]"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-48">
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader size={16} className="animate-spin text-[#009DB9]" />
                  <span className="ml-2 text-[12px] text-neutral-500">Loading users...</span>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="py-4 text-center text-[12px] text-neutral-400">
                  No users found
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelect(user)}
                    className={`w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors ${
                      value === user.id ? "bg-[#009DB9]/5" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[13px] font-medium text-neutral-800">
                          {user.firstName} {user.lastName}
                        </span>
                        {user.email && (
                          <p className="text-[10px] text-neutral-400 mt-0.5">{user.email}</p>
                        )}
                      </div>
                      {value === user.id && (
                        <div className="w-2 h-2 rounded-full bg-[#009DB9]" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Searchable Country Dropdown Component ──────────────────────────────────────
function SearchableCountryDropdown({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const filteredCountries = africanCountries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const selectedCountry = africanCountries.find(c => c.name === value);
  
  return (
    <div className="relative">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-8 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none transition-colors flex items-center justify-between cursor-pointer bg-white hover:border-[#009DB9] focus:border-[#009DB9]"
      >
        <span className={!value ? "text-neutral-400" : "text-neutral-800"}>
          {selectedCountry?.name || "Select a country..."}
        </span>
        <ChevronDown size={14} className={`text-neutral-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </div>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 w-full mt-1 overflow-hidden bg-white border rounded-lg shadow-lg border-neutral-200">
            <div className="p-2 border-b border-neutral-100">
              <div className="relative">
                <Search size={14} className="absolute -translate-y-1/2 left-3 top-1/2 text-neutral-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search country..."
                  className="w-full h-8 pl-8 pr-3 text-[12px] border border-neutral-200 rounded-lg outline-none focus:border-[#009DB9]"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-48">
              {filteredCountries.length === 0 ? (
                <div className="py-4 text-center text-[12px] text-neutral-400">
                  No countries found
                </div>
              ) : (
                filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => {
                      onChange(country.name);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    className={`w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors ${
                      value === country.name ? "bg-[#009DB9]/5" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-medium text-neutral-800">
                        {country.name}
                      </span>
                      {value === country.name && (
                        <div className="w-2 h-2 rounded-full bg-[#009DB9]" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Small DealCard for the view tab ─────────────────────────────────────────
function DealCard({ deal, onEdit }) {
  const s = DEAL_STAGE_STYLES[deal.stage] || { color: "#6b7280", bg: "#f3f4f6" };
  const isWon  = deal.stage === "CLOSED_WON";
  const isLost = deal.stage === "CLOSED_LOST";

  return (
    <div className="p-4 transition-shadow bg-white border border-neutral-100 rounded-xl hover:shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[13px] font-semibold text-neutral-800 leading-tight">{deal.title}</span>
        <button
          onClick={() => onEdit(deal)}
          className="flex-shrink-0 text-[10px] font-semibold px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors"
          style={{ background: "rgba(0,157,185,0.1)", color: "#009DB9" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.2)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.1)")}
        >
          Edit <ArrowRight size={9} strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span
          className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
          style={{ background: s.bg, color: s.color }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
          {deal.stage}
        </span>
        {deal.probability !== undefined && (
          <span className="inline-flex items-center gap-1 text-[10px] text-neutral-400">
            <Percent size={9} />{deal.probability}%
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {deal.value ? (
          <div className="flex items-center gap-1">
            <DollarSign size={11} className={isWon ? "text-emerald-500" : "text-neutral-400"} />
            <span className={`text-[12px] font-bold ${isWon ? "text-emerald-600" : "text-neutral-700"}`}>
              {deal.currency || ""} {Number(deal.value).toLocaleString()}
            </span>
          </div>
        ) : (
          <span className="text-[11px] text-neutral-300">No value set</span>
        )}
        {deal.expectedCloseDate && (
          <div className="flex items-center gap-1 text-[10px] text-neutral-400">
            <Calendar size={9} />
            {new Date(deal.expectedCloseDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
        )}
      </div>

      {isLost && deal.lostReason && (
        <div className="pt-2 mt-2 border-t border-red-100">
          <p className="text-[10px] text-red-500 line-clamp-2">{deal.lostReason}</p>
        </div>
      )}
    </div>
  );
}

// ─── Helper functions for contact persons ──────────────────────────────────
function getPersonFullName(person) {
  if (!person) return "";
  return person.fullName || `${person.firstName || ""} ${person.lastName || ""}`.trim();
}

function getPersonInitials(person) {
  if (!person) return "?";
  if (person.firstName && person.lastName) {
    return (person.firstName.charAt(0) + person.lastName.charAt(0)).toUpperCase();
  }
  if (person.fullName) {
    return person.fullName.substring(0, 2).toUpperCase();
  }
  return "?";
}

function getPersonDisplayInfo(person) {
  if (!person) return { name: "", initials: "?", contactInfo: "" };
  const name = getPersonFullName(person);
  const initials = getPersonInitials(person);
  const contactInfo = person.email || person.phone || person.mobile || "No contact info";
  return { name, initials, contactInfo };
}

// ─── Main ContactDrawer ───────────────────────────────────────────────────────
export default function ContactDrawer({
  open,
  onClose,
  mode,
  contact,
  onSubmit,
  onArchive,
}) {
  const { user } = useAuth();
  const isAdd  = mode === "add";
  const isEdit = mode === "edit";
  const isView = mode === "view";

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const canAssignUsers = () => {
    if (!user) return false;
    const role = user.role;
    return role === "SUPER_ADMIN" || role === "ADMIN" || role === "MANAGER";
  };

  const fetchUsers = useCallback(async () => {
    if (!canAssignUsers()) return;
    if (usersFetched) return;
    
    setLoadingUsers(true);
    try {
      const res = await listUsers({ size: 100 });
      const usersList = Array.isArray(res) ? res : res.content || [];
      const salesReps = usersList.filter(u => u.role === "SALES_REP" || u.role === "EMPLOYEE");
      setUsers(salesReps);
      setUsersFetched(true);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [canAssignUsers, usersFetched]);

  useEffect(() => {
    if (open && (isAdd || isEdit) && canAssignUsers() && !usersFetched) {
      fetchUsers();
    }
  }, [open, isAdd, isEdit, canAssignUsers, usersFetched, fetchUsers]);

  // ── Contact persons: fetched directly from the API, not from a prop ──
  const [persons, setPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showAllPersons, setShowAllPersons] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);

  const fetchPersons = useCallback(async () => {
    if (!contact?.id) return;
    setLoadingPersons(true);
    try {
      const data = await getContactPersons({ contactId: contact.id });
      setPersons(data || []);
    } catch (err) {
      console.error("Failed to fetch contact persons:", err);
      setPersons([]);
    } finally {
      setLoadingPersons(false);
    }
  }, [contact?.id]);

  useEffect(() => {
    if (open && isView && contact?.id) {
      fetchPersons();
    }
    if (!open) {
      setPersons([]);
      setEditingPerson(null);
    }
  }, [open, isView, contact?.id, fetchPersons]);

  // Handles both create (POST) and update (PUT) depending on editingPerson.
  // AddContactPersonModal awaits this and shows its own error banner if it
  // throws, and closes itself on success.
  const handleSavePerson = async (personData) => {
    if (editingPerson) {
      await updateContactPerson({
        contactId: contact.id,
        personId: editingPerson.id,
        ...personData,
      });
    } else {
      await addContactPerson({
        contactId: contact.id,
        ...personData,
      });
    }
    await fetchPersons();
  };

  const handleEditPerson = (person) => {
    setEditingPerson(person);
    setShowAddPerson(true);
  };

  const handleDeletePerson = async (personId) => {
    try {
      await deleteContactPerson({ contactId: contact.id, personId });
      await fetchPersons();
    } catch (err) {
      console.error("Failed to delete contact person:", err);
      alert(err.response?.data?.message || "Failed to delete contact person");
    }
  };

  const handleSetPrimary = async (personId) => {
    try {
      await setPrimaryContactPerson({ contactId: contact.id, personId });
      await fetchPersons();
    } catch (err) {
      console.error("Failed to set primary contact:", err);
      alert(err.response?.data?.message || "Failed to set primary contact");
    }
  };

  // ── Form state (add / edit) ──
  const emptyForm = {
    firstName: "", lastName: "", company: "", email: "",
    phone: "", jobTitle: "", industry: "",
    address: "", country: "",
    website: "", linkedIn: "", notes: "", assignedTo: "",
  };
  const [form, setForm] = useState(emptyForm);

  // ADDED: error state for the add/edit form
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // CHANGED: clears that field's error as the user edits it
  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    clearFieldError(key);
  };

  const inputClass = (key, base) =>
    `${base} ${
      fieldErrors[key]
        ? "border-red-400 focus:border-red-500 bg-red-50/40"
        : "border-neutral-200 focus:border-[#009DB9]"
    }`;

  const [viewTab, setViewTab] = useState("info");
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [dealForm, setDealForm] = useState({ open: false, deal: null });

  useEffect(() => {
    if (!open) {
      setUsersFetched(false);
      setUsers([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setViewTab("info");
    setDeals([]);
    setFormError("");
    setFieldErrors({});

    if (isEdit && contact) {
      // Extract address and country from contact
      let address = "";
      let country = "";
      
      if (typeof contact.address === "string") {
        const parts = contact.address.split(",").map(s => s.trim());
        const lastPart = parts[parts.length - 1];
        const foundCountry = africanCountries.find(c => 
          c.name.toLowerCase() === lastPart.toLowerCase()
        );
        if (foundCountry) {
          country = foundCountry.name;
          address = parts.slice(0, -1).join(", ");
        } else {
          address = contact.address;
        }
      } else if (contact.address) {
        address = contact.address?.street || "";
        country = contact.address?.country || "";
      }

      setForm({
        firstName: contact.firstName || "",
        lastName:  contact.lastName  || "",
        company:   contact.company_name || contact.company || "",
        email:     contact.email    || "",
        phone:     contact.phone    || "",
        jobTitle:  contact.jobTitle || contact.role || "",
        industry:  contact.industry || "",
        address:   address,
        country:   country,
        website:   contact.website  || "",
        linkedIn:  contact.linkedIn || "",
        notes:     contact.notes    || "",
        assignedTo: contact.assignedTo || "",
      });
    } else {
      setForm(emptyForm);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, contact]);

  const fetchDeals = async () => {
    if (!contact?.id) return;
    setLoadingDeals(true);
    try {
      const res = await getDealsByContact(contact.id);
      setDeals(Array.isArray(res) ? res : res.content || []);
    } catch (err) {
      console.error("Failed to fetch deals:", err);
      setDeals([]);
    } finally {
      setLoadingDeals(false);
    }
  };

  const handleTabChange = (tab) => {
    setViewTab(tab);
    if (tab === "deals") fetchDeals();
  };

  // REMOVED isFormValid - we want the button to always be clickable

  // CHANGED: async, awaits onSubmit, only resets/closes on success,
  // parses backend validationErrors into fieldErrors on failure, and
  // never closes the drawer or clears the form when the backend rejects.
  const handleSubmit = async () => {
    setFormError("");
    setFieldErrors({});

    // ADDED: Frontend validation - all fields are now required
    const newFieldErrors = {};
    if (!form.firstName?.trim()) {
      newFieldErrors.firstName = "First Name is required";
    }
    if (!form.lastName?.trim()) {
      newFieldErrors.lastName = "Last Name is required";
    }
    if (!form.company?.trim()) {
      newFieldErrors.company = "Company Name is required";
    }
    if (!form.email?.trim()) {
      newFieldErrors.email = "Email is required";
    } else if (!isValidEmail(form.email.trim())) {
      newFieldErrors.email = "Please enter a valid email address";
    }
    if (!form.phone?.trim()) {
      newFieldErrors.phone = "Phone is required";
    }
    if (!form.industry?.trim()) {
      newFieldErrors.industry = "Industry is required";
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }

    // Build full address with country
    let fullAddress = form.address.trim();
    if (form.country) {
      fullAddress = fullAddress ? `${fullAddress}, ${form.country}` : form.country;
    }

    const submitData = {
      firstName:     form.firstName.trim(),
      lastName:      form.lastName.trim(),
      company:       form.company.trim(),
      email:         form.email.trim(),
      phone:         form.phone.trim(),
      jobTitle:      form.jobTitle.trim(),
      industry:      form.industry.trim(),
      address:       fullAddress,
      website:       form.website.trim(),
      linkedIn:      form.linkedIn.trim(),
      notes:         form.notes.trim(),
      status:        isEdit ? contact.status : "PROSPECT",
      pipelineStage: isEdit ? (contact.pipelineStage || "PROSPECTING") : "PROSPECTING",
    };

    if (canAssignUsers() && form.assignedTo) {
      submitData.assignedTo = form.assignedTo;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(submitData);
      setForm(emptyForm);
      setFieldErrors({});
      setFormError("");
      onClose();
    } catch (err) {
      console.error("Error submitting form:", err);
      const backendFieldErrors = err?.response?.data?.validationErrors || {};
      if (Object.keys(backendFieldErrors).length > 0) {
        setFieldErrors(backendFieldErrors);
        setFormError("");
      } else {
        const data = err?.response?.data;
        setFormError(data?.message || data?.error || err?.message || "Failed to save company. Please try again.");
      }
      // drawer stays open, form stays populated — no onClose(), no reset
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const pipelineStage = contact?.pipelineStage || "PROSPECTING";
  const stageInfo = STAGES.find((s) => s.id === pipelineStage.toLowerCase()) ||
                    STAGES.find((s) => s.id === "prospecting");
  const hasMany = persons.length > 1;
  const hasPrimaryContact = !!(contact?.firstName || contact?.lastName);
  const primaryContactName = `${contact?.firstName || ""} ${contact?.lastName || ""}`.trim();
  const primaryContactInitials = primaryContactName
    ? primaryContactName.split(" ").filter(Boolean).map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "";

  const getAssignedUserName = () => {
    const assignedUser = users.find(u => u.id === contact?.assignedTo);
    if (assignedUser) {
      return `${assignedUser.firstName} ${assignedUser.lastName}`;
    }
    return contact?.assignedTo || "";
  };

  // Get first person display info
  const firstPerson = persons[0];
  const firstPersonInfo = firstPerson ? getPersonDisplayInfo(firstPerson) : { name: "", initials: "?", contactInfo: "" };
  const firstPersonName = firstPerson ? getPersonFullName(firstPerson) : "";

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.4)" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="flex flex-col w-full mx-4 bg-white shadow-2xl rounded-2xl md:mx-0"
          style={{ maxHeight: "90vh", maxWidth: isView ? "560px" : "512px" }}
        >
          {/* ── Header ── */}
          <div className="flex items-start justify-between flex-shrink-0 px-4 py-4 border-b border-neutral-100 md:px-6">
            <div className="flex items-center flex-1 min-w-0 gap-3">
              {isView && contact && (
                <div
                  className="w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center text-[12px] md:text-[14px] font-bold flex-shrink-0"
                  style={{ background: contact.logoColor?.bg || "#e0f2fe", color: contact.logoColor?.color || "#0284c7" }}
                >
                  {contact.logo}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-[14px] md:text-[15px] font-bold text-neutral-900 truncate">
                  {isAdd  && "Add New Company"}
                  {isEdit && `Edit Company — ${form.company || contact?.company_name}`}
                  {isView && contact?.company_name}
                </h2>
                <p className="text-[10px] md:text-[11px] text-neutral-400 mt-0.5 truncate">
                  {isAdd  && "New company will be added to Prospecting"}
                  {isEdit && "Update company information"}
                  {isView && contact && (
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      {contact.industry && <span>{contact.industry}</span>}
                      {contact.industry && <span className="hidden text-neutral-300 sm:inline">·</span>}
                      <span
                        className="inline-flex items-center gap-1 text-[10px] md:text-[11px] font-semibold rounded-full px-2 py-0.5"
                        style={{ background: (stageInfo?.color || "#8B5CF6") + "18", color: stageInfo?.color || "#8B5CF6" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageInfo?.color || "#8B5CF6" }} />
                        {stageInfo?.name || pipelineStage}
                      </span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center flex-shrink-0 rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100"
            >
              <X size={15} />
            </button>
          </div>

          {/* ── View Tabs ── */}
          {isView && (
            <div className="flex flex-shrink-0 px-4 overflow-x-auto border-b border-neutral-100 md:px-6">
              {[
                { id: "info",  label: "Info"  },
                { id: "deals", label: "Deals" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleTabChange(t.id)}
                  className="px-3 py-3 text-[11px] md:text-[12px] font-semibold border-b-2 transition-all whitespace-nowrap"
                  style={
                    viewTab === t.id
                      ? { color: "#009DB9", borderColor: "#009DB9" }
                      : { color: "#a3a3a3", borderColor: "transparent" }
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* ── Body ── */}
          <div className="flex-1 overflow-y-auto">
            {(isAdd || isEdit) && (
              <div className="flex flex-col gap-4 px-4 py-5 md:px-6">

                {/* ADDED: generic error banner, only when no field could be matched */}
                {formError && Object.keys(fieldErrors).length === 0 && (
                  <div className="p-3 text-sm text-red-600 border border-red-200 rounded-lg bg-red-50">
                    {formError}
                  </div>
                )}

                {/* ── Subsection 1: Company Info ── */}
                <SectionTitle>Company Info</SectionTitle>

                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Company Name *</label>
                  {fieldErrors.company && <p className="text-[11px] text-red-600">{fieldErrors.company}</p>}
                  <input value={form.company} onChange={(e) => set("company", e.target.value)}
                    placeholder="e.g. Google Kenya"
                    className={inputClass("company", "h-9 border rounded-lg px-3 text-[13px] outline-none transition-colors")} />
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Email *</label>
                    {fieldErrors.email && <p className="text-[11px] text-red-600">{fieldErrors.email}</p>}
                    <input value={form.email} onChange={(e) => set("email", e.target.value)}
                      placeholder="contact@company.com"
                      className={inputClass("email", "h-8 border rounded-lg px-3 text-[12px] outline-none transition-colors")} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Phone *</label>
                    {fieldErrors.phone && <p className="text-[11px] text-red-600">{fieldErrors.phone}</p>}
                    <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                      placeholder="+254 7XX XXX XXX"
                      className={inputClass("phone", "h-8 border rounded-lg px-3 text-[12px] outline-none transition-colors")} />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Industry *</label>
                  {fieldErrors.industry && <p className="text-[11px] text-red-600">{fieldErrors.industry}</p>}
                  <input value={form.industry} onChange={(e) => set("industry", e.target.value)}
                    placeholder="e.g. IT, SaaS"
                    className={inputClass("industry", "h-8 border rounded-lg px-3 text-[12px] outline-none transition-colors")} />
                </div>

                {/* Address and Country fields */}
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Address</label>
                    {fieldErrors.address && <p className="text-[11px] text-red-600">{fieldErrors.address}</p>}
                    <input value={form.address} onChange={(e) => set("address", e.target.value)}
                      placeholder="e.g. Westlands, Nairobi"
                      className={inputClass("address", "h-8 border rounded-lg px-3 text-[12px] outline-none transition-colors")} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Country</label>
                    {fieldErrors.country && <p className="text-[11px] text-red-600">{fieldErrors.country}</p>}
                    <SearchableCountryDropdown
                      value={form.country}
                      onChange={(country) => set("country", country)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Website</label>
                    {fieldErrors.website && <p className="text-[11px] text-red-600">{fieldErrors.website}</p>}
                    <input value={form.website} onChange={(e) => set("website", e.target.value)}
                      placeholder="https://company.com"
                      className={inputClass("website", "h-8 border rounded-lg px-3 text-[12px] outline-none transition-colors")} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">LinkedIn</label>
                    {fieldErrors.linkedIn && <p className="text-[11px] text-red-600">{fieldErrors.linkedIn}</p>}
                    <input value={form.linkedIn} onChange={(e) => set("linkedIn", e.target.value)}
                      placeholder="linkedin.com/in/profile"
                      className={inputClass("linkedIn", "h-8 border rounded-lg px-3 text-[12px] outline-none transition-colors")} />
                  </div>
                </div>

                {canAssignUsers() && (
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide flex items-center gap-1">
                      <UserCheck size={11} /> Assign To
                    </label>
                    <SearchableUserDropdown
                      value={form.assignedTo}
                      onChange={(userId) => set("assignedTo", userId)}
                      users={users}
                      isLoading={loadingUsers}
                      disabled={false}
                    />
                    <p className="text-[10px] text-neutral-400 mt-0.5">
                      Assign this company to a sales representative
                    </p>
                  </div>
                )}

                <SectionDivider />

                {/* ── Subsection 2: Company Contact Person ── */}
                <SectionTitle>Company Contact Person</SectionTitle>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">First Name *</label>
                    {fieldErrors.firstName && <p className="text-[11px] text-red-600">{fieldErrors.firstName}</p>}
                    <input value={form.firstName} onChange={(e) => set("firstName", e.target.value)}
                      placeholder="e.g. John"
                      className={inputClass("firstName", "h-9 border rounded-lg px-3 text-[13px] outline-none transition-colors")} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Last Name *</label>
                    {fieldErrors.lastName && <p className="text-[11px] text-red-600">{fieldErrors.lastName}</p>}
                    <input value={form.lastName} onChange={(e) => set("lastName", e.target.value)}
                      placeholder="e.g. Doe"
                      className={inputClass("lastName", "h-9 border rounded-lg px-3 text-[13px] outline-none transition-colors")} />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Job Title</label>
                  {fieldErrors.jobTitle && <p className="text-[11px] text-red-600">{fieldErrors.jobTitle}</p>}
                  <input value={form.jobTitle} onChange={(e) => set("jobTitle", e.target.value)}
                    placeholder="e.g. VP Engineering"
                    className={inputClass("jobTitle", "h-8 border rounded-lg px-3 text-[12px] outline-none transition-colors")} />
                </div>

                <SectionDivider />

                {/* ── Subsection 3: Note ── */}
                <SectionTitle>Note</SectionTitle>

                <div className="flex flex-col gap-1">
                  {fieldErrors.notes && <p className="text-[11px] text-red-600">{fieldErrors.notes}</p>}
                  <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    placeholder="Additional notes about the company..."
                    className={`${inputClass("notes", "border rounded-lg px-3 py-2 text-[12px] outline-none transition-colors")} resize-none`} />
                </div>
              </div>
            )}

            {isView && viewTab === "info" && contact && (
              <div className="flex flex-col gap-5 px-4 py-5 md:px-6">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    { icon: MapPin,    label: "Address",       value: [contact.address?.street, contact.address?.region, contact.address?.country].filter(Boolean).join(", ") },
                    { icon: Link,      label: "Website",       value: contact.website, isLink: true },
                    { icon: Phone,     label: "Phone",         value: contact.phone },
                    { icon: Mail,      label: "Email",         value: contact.email },
                    { icon: Briefcase, label: "Job Title",     value: contact.jobTitle },
                    { icon: Briefcase, label: "Industry",      value: contact.industry },
                    { icon: Users,     label: "Company Size",  value: contact.size ? `${contact.size} employees` : null },
                    ...(contact.assignedTo ? [{ icon: UserCheck, label: "Assigned To", value: getAssignedUserName() }] : []),
                  ].map(({ icon: Icon, label, value, isLink }, i) => (
                    value && (
                      <div key={i} className="flex items-start gap-2.5 p-3 bg-neutral-50 rounded-xl">
                        <Icon size={13} className="text-neutral-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">{label}</div>
                          {isLink ? (
                            <a
                              href={normalizeUrl(value)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[12px] font-medium text-[#009DB9] hover:underline break-words"
                            >
                              {value}
                            </a>
                          ) : (
                            <div className="text-[12px] font-medium text-neutral-800 break-words">{value}</div>
                          )}
                        </div>
                      </div>
                    )
                  ))}
                </div>

                {hasPrimaryContact && (
                  <div>
                    <SectionTitle>Primary Contact</SectionTitle>
                    <div className="flex flex-col gap-3 p-3.5 mt-3 bg-white border border-neutral-100 rounded-xl sm:flex-row sm:items-start">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 self-start"
                        style={{ background: "#009DB9" }}>
                        {primaryContactInitials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-neutral-900">{primaryContactName}</div>
                        {contact.jobTitle && (
                          <div className="text-[11px] text-neutral-400 mt-0.5">{contact.jobTitle}</div>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-neutral-500">
                          {contact.phone && <span className="flex items-center gap-1"><Phone size={9} />{contact.phone}</span>}
                          {contact.email && <span className="flex items-center gap-1"><Mail size={9} />{contact.email}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {contact.notes && (
                  <div>
                    <SectionTitle>Notes</SectionTitle>
                    <div className="p-3.5 mt-3 bg-neutral-50 rounded-xl">
                      <p className="text-[12px] text-neutral-700 leading-relaxed whitespace-pre-wrap break-words">
                        {contact.notes}
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Contact Persons Section ── */}
                <div>
                  <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-[13px] font-bold text-neutral-800">
                      Contact Persons
                      <span className="ml-1.5 text-[11px] font-semibold text-neutral-400">({persons.length})</span>
                    </h3>
                    <div className="flex items-center gap-2">
                      {hasMany && (
                        <button onClick={() => setShowAllPersons(true)}
                          className="flex items-center gap-1 text-[11px] font-semibold text-[#009DB9] hover:underline">
                          View all <ChevronRight size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => { setEditingPerson(null); setShowAddPerson(true); }}
                        className="flex items-center gap-1.5 text-[11px] font-semibold px-3 h-7 rounded-lg border transition-colors"
                        style={{ color: "#009DB9", borderColor: "#009DB9" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.06)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <UserPlus size={12} /> Add Person
                      </button>
                    </div>
                  </div>

                  {loadingPersons ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader size={22} className="animate-spin text-[#009DB9]" />
                    </div>
                  ) : persons.length === 0 ? (
                    <div className="py-6 text-center border border-dashed border-neutral-200 rounded-xl">
                      <UserPlus size={20} className="text-neutral-300 mx-auto mb-1.5" />
                      <p className="text-[12px] text-neutral-400">No contact persons yet</p>
                    </div>
                  ) : (
                    <div className="flex flex-col p-3.5 border border-neutral-100 rounded-xl bg-white sm:flex-row sm:items-start gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 self-start"
                        style={{ background: "#009DB9" }}>
                        {firstPersonInfo.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[13px] font-semibold text-neutral-900">{firstPersonInfo.name}</span>
                            {firstPerson.primaryContact && (
                              <span className="text-[10px] font-semibold text-[#009DB9] bg-[#009DB9]/10 px-2 py-0.5 rounded-full">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {hasMany && (
                              <button onClick={() => setShowAllPersons(true)}
                                className="text-[11px] font-bold rounded-full px-2.5 py-1 transition-colors self-start sm:self-auto"
                                style={{ background: "rgba(0,157,185,0.1)", color: "#009DB9" }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.18)")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.1)")}>
                                +{persons.length - 1} more
                              </button>
                            )}
                            <button
                              onClick={() => handleEditPerson(firstPerson)}
                              className="p-1.5 text-neutral-400 hover:text-[#009DB9] hover:bg-neutral-100 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete ${firstPersonName}?`)) {
                                  handleDeletePerson(firstPerson.id);
                                }
                              }}
                              className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        <div className="text-[11px] text-neutral-400 mt-0.5 break-all">
                          {firstPersonInfo.contactInfo}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-neutral-500">
                          {firstPerson.jobTitle && <span>{firstPerson.jobTitle}</span>}
                          {firstPerson.department && <span>· {firstPerson.department}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isView && viewTab === "deals" && (
              <div className="flex flex-col gap-3 px-4 py-5 md:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[12px] text-neutral-400">
                    All deals for <strong className="text-neutral-700">{contact?.company_name}</strong>
                  </p>
                  <button
                    onClick={() => setDealForm({ open: true, deal: null })}
                    className="flex items-center justify-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-xl text-white transition-colors"
                    style={{ background: "#009DB9" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#007a90")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#009DB9")}
                  >
                    <Plus size={13} /> Add Deal
                  </button>
                </div>

                {loadingDeals ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader size={24} className="animate-spin text-[#009DB9]" />
                  </div>
                ) : deals.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {deals.map((deal) => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        onEdit={(d) => setDealForm({ open: true, deal: d })}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center bg-neutral-50 rounded-xl">
                    <FileText size={32} className="mx-auto mb-2 text-neutral-300" />
                    <p className="text-[13px] text-neutral-500">No deals yet</p>
                    <p className="text-[11px] text-neutral-400 mt-1">
                      Click "Add Deal" to create the first deal for this company
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex-shrink-0 px-4 py-4 border-t border-neutral-100 md:px-6">
            {(isAdd || isEdit) && (
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button onClick={onClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg text-[12px] font-medium text-neutral-600 border border-neutral-200 hover:bg-neutral-50 sm:py-1.5 disabled:opacity-40">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}  // ✅ ONLY disabled while submitting
                  className="px-4 py-2 rounded-lg text-[12px] font-semibold text-white disabled:opacity-40 sm:py-1.5"
                  style={{ background: "#009DB9" }}
                  onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = "#007a90"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
                >
                  {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Add Company"}
                </button>
              </div>
            )}
            {isView && (
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <button onClick={onClose}
                  className="w-full py-2 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors sm:flex-1 sm:py-2.5">
                  Close
                </button>
                {onArchive && (
                  <button
                    onClick={() => onArchive(contact)}
                    className="flex items-center justify-center w-full gap-1.5 py-2 rounded-xl text-[13px] font-semibold border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors sm:flex-1 sm:py-2.5"
                  >
                    <Archive size={14} /> Archive
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isView && (
        <>
          <AddContactPersonModal
            open={showAddPerson}
            onClose={() => {
              setShowAddPerson(false);
              setEditingPerson(null);
            }}
            onAdd={handleSavePerson}
            initialData={editingPerson}
          />
          <AllContactPersonsModal
            open={showAllPersons}
            onClose={() => setShowAllPersons(false)}
            company={contact}
            persons={persons}
            onAddPerson={() => {
              setShowAllPersons(false);
              setEditingPerson(null);
              setShowAddPerson(true);
            }}
            onEditPerson={(person) => {
              setShowAllPersons(false);
              handleEditPerson(person);
            }}
            onDeletePerson={handleDeletePerson}
            onSetPrimary={handleSetPrimary}
          />
          <DealFormModal
            open={dealForm.open}
            contact={contact}
            deal={dealForm.deal}
            onClose={() => setDealForm({ open: false, deal: null })}
            onSuccess={fetchDeals}
          />
        </>
      )}
    </>
  );
}